import { useNavigate } from "react-router-dom";

import DeviceUnknownIcon from "@mui/icons-material/DeviceUnknown";
import LaunchIcon from "@mui/icons-material/Launch";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";
import useTheme from "@mui/material/styles/useTheme";

import { misc, neutral } from "app/themes/colors";
import useSunoDialer from "components/suno-dialer/use-suno-dialer.hook";
import { BAS } from "constants/patients";
import { formatPhoneNumber } from "utils/common";

export default function TitleBlock() {
  const theme = useTheme();
  const navigate = useNavigate();

  const { incomingCallDetails, isExpanded, handleToggleExpand, patient } =
    useSunoDialer();
  const { callerId, extra } = incomingCallDetails || {};

  const displayName = patient?.fullName
    ? patient?.fullName
    : extra?.phone_number
      ? formatPhoneNumber(String(extra?.phone_number))
      : "Unknown Caller";

  return (
    <Stack
      justifyContent="space-between"
      direction="row"
      sx={{ p: 2, pt: 2.5, pb: 0 }}
    >
      <Stack
        sx={{
          background: "white",
          p: isExpanded ? 1.5 : 1,
          px: isExpanded ? undefined : 2,
          pb: isExpanded ? undefined : 1,
          borderRadius: "16px",
          boxShadow: theme.shadows[3],
          width: "100%",
          cursor: "pointer",
          "&:hover": {
            boxShadow: theme.shadows[0]
          }
        }}
        onClick={handleToggleExpand}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          {isExpanded && (
            <>
              {Boolean(callerId) ? (
                <Avatar
                  alt="avatar"
                  src={patient?.avatar || ""}
                  sx={{
                    cursor: "pointer",
                    width: "70px",
                    height: "70px"
                  }}
                  slotProps={{ img: { loading: "lazy" } }}
                />
              ) : (
                <Avatar
                  alt="avatar"
                  sx={{
                    cursor: "pointer",
                    width: "70px",
                    height: "70px",
                    background: grey[300]
                  }}
                  slotProps={{ img: { loading: "lazy" } }}
                >
                  <DeviceUnknownIcon
                    sx={{ fontSize: "34px", color: neutral[700] }}
                  />
                </Avatar>
              )}
            </>
          )}

          <Stack
            width="100%"
            height="100%"
            alignItems={isExpanded ? undefined : "center"}
            direction={isExpanded ? "column" : "row"}
            spacing={isExpanded ? undefined : 1}
            sx={{ pl: isExpanded ? 1 : undefined }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: isExpanded ? "17px" : "15px",
                pb: isExpanded ? 1 : 0.7,
                pr: 1
              }}
            >
              Incoming&nbsp;Call…
            </Typography>

            <Link
              href={callerId ? `/patients/${callerId}` : undefined}
              sx={{
                width: "fit-content",
                color: neutral[900],
                textDecorationColor: neutral[500],
                textDecoration: callerId ? undefined : "none",
                "&:hover": {
                  textDecoration: callerId ? misc.purpleText : undefined
                }
              }}
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                if (callerId) {
                  navigate(`/patients/${callerId}`);
                }
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontSize: isExpanded ? "17px" : "14px",
                  lineHeight: isExpanded ? 1.3 : 1.1
                }}
              >
                {displayName}
                {Boolean(callerId) && (
                  <IconButton
                    size="small"
                    sx={{
                      ml: -0.5,
                      mt: -0.8,
                      transform: "scale(0.6)",
                      opacity: 0.5,
                      "&:hover": {
                        color: misc.purpleText,
                        opacity: 1
                      }
                    }}
                  >
                    <LaunchIcon />
                  </IconButton>
                )}
              </Typography>
            </Link>
            {isExpanded && Boolean(patient?.sex || patient?.age_years) && (
              <Typography
                sx={{
                  fontSize: "13px",
                  lineHeight: 1.1,
                  color: grey[800]
                }}
              >
                {[
                  BAS.getFromCode(patient?.sex),
                  patient?.age_years ? `${patient?.age_years} years old` : ""
                ]
                  .filter(Boolean)
                  .join(", ")}
              </Typography>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
}
